import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from './config.js';
import { DPR, canvasSize } from './ui/dpr.js';
import { installRetina } from './ui/retina.js';
import { installGameFont, refreshTexts } from './ui/gameFont.js';
import { BootScene } from './scenes/BootScene.js';
import { PreloadScene } from './scenes/PreloadScene.js';
import { LoginScene } from './scenes/LoginScene.js';
import { MainScene } from './scenes/MainScene.js';
import { HomeScene } from './scenes/HomeScene.js';
import { FarmScene } from './scenes/FarmScene.js';
import { ShopScene } from './scenes/ShopScene.js';
import { OutsideScene } from './scenes/OutsideScene.js';
import { KitchenScene } from './scenes/KitchenScene.js';
import { SalonScene } from './scenes/SalonScene.js';
import { SecondFloorScene } from './scenes/SecondFloorScene.js';
import { PetShopScene } from './scenes/PetShopScene.js';
import { FurnitureShopScene } from './scenes/FurnitureShopScene.js';
import { ClothingShopScene } from './scenes/ClothingShopScene.js';
import { BikeShopScene } from './scenes/BikeShopScene.js';
import { BackyardScene } from './scenes/BackyardScene.js';
import { GameHallScene } from './scenes/GameHallScene.js';
import { GameHallUpstairsScene } from './scenes/GameHallUpstairsScene.js';
import { BoardGameScene } from './scenes/BoardGameScene.js';
import { DrivingSchoolScene } from './scenes/DrivingSchoolScene.js';
import { CarDealerScene } from './scenes/CarDealerScene.js';
import { TrainingRouteScene } from './scenes/TrainingRouteScene.js';
import { VisitMainScene } from './scenes/visit/VisitMainScene.js';
import { VisitHomeScene } from './scenes/visit/VisitHomeScene.js';
import { VisitKitchenScene } from './scenes/visit/VisitKitchenScene.js';
import { VisitSalonScene } from './scenes/visit/VisitSalonScene.js';
import { VisitSecondFloorScene } from './scenes/visit/VisitSecondFloorScene.js';
import { VisitFarmScene } from './scenes/visit/VisitFarmScene.js';
import { VisitBackyardScene } from './scenes/visit/VisitBackyardScene.js';
import { SizeLabScene } from './scenes/SizeLabScene.js';
import { mountAudioControls } from './audio/AudioControls.js';
import { mountMobileShell } from './ui/mobileShell.js';
import { mountRotatePrompt } from './ui/rotatePrompt.js';
import { installDiagBeacon } from './dev/diagBeacon.js';
import { gameState } from './state/gameState.js';
import { idleTracker } from './state/idleTracker.js';
import { matchClient } from './api/matchClient.js';
import { inactivityEffects, matchHoldsInactive } from './state/inactivityEffects.mjs';
import { showInactiveOverlay, hideInactiveOverlay } from './ui/inactiveOverlay.js';
import { realtime } from './api/realtime.js';
import { virtualInput } from './ui/virtualInput.js';
import { iconHtml } from './ui/iconHtml.mjs';
import { openSettings, topScene, sessionLost } from './ui/SettingsModal.js';
import { setSessionLostHandler } from './api/client.js';
import { installInputLockReset, isInputLocked } from './ui/inputLock.js';
import { installAutoUpdate } from './ui/autoUpdate.js';
import { LookPipeline } from './look/LookPipeline.js';

const gameFontReady = installGameFont();   // Grandstander (ui/gameFont.js)
const { width: canvasW, height: canvasH } = canvasSize(GAME_WIDTH, GAME_HEIGHT);
const game = new Phaser.Game({
  type: Phaser.AUTO,
  parent: 'game-root',
  width: canvasW,
  height: canvasH,
  backgroundColor: '#0f1027',
  scale: {
    mode: Phaser.Scale.FIT,
    autoCenter: Phaser.Scale.CENTER_BOTH,
  },
  render: {
    antialias: true,
    premultipliedAlpha: false,  // PNG-i z gpt-image-1 mają non-premultiplied alpha — bez tej flagi WebGL pokazuje ciemne kropki na krawędziach
    transparent: false,
    roundPixels: true,
  },
  dom: { createContainer: true },
  // the player's look (skin / eyes / hair recolour); Phaser registers it only under WebGL
  pipeline: { Look: LookPipeline },
  scene: [BootScene, PreloadScene, LoginScene, MainScene, HomeScene, FarmScene, ShopScene, OutsideScene,
          KitchenScene, SalonScene, SecondFloorScene,
          PetShopScene, FurnitureShopScene, ClothingShopScene, BikeShopScene,
          BackyardScene,
          GameHallScene, GameHallUpstairsScene, BoardGameScene,
          DrivingSchoolScene, CarDealerScene, TrainingRouteScene,
          VisitMainScene, VisitHomeScene, VisitKitchenScene, VisitSalonScene,
          VisitSecondFloorScene, VisitFarmScene, VisitBackyardScene,
          ...(import.meta.env.DEV ? [SizeLabScene] : [])],
});
installRetina(game);
// Size the game to the window's real height. Chrome on iOS resolves 100dvh to more than it
// shows (an iPhone 14 Pro in landscape: 337 px visible, the game drawn ~412 px tall), so the
// canvas overflowed top and bottom and hid the farm's bottom row (Powiększ) and the Rynek's top.
const gameRoot = document.getElementById('game-root');
const fitRoot = () => {
  const h = `${Math.round(window.innerHeight)}px`;
  if (gameRoot.style.height === h) return;
  gameRoot.style.height = h;
  game.scale.refresh();
};
window.addEventListener('resize', fitRoot);
window.visualViewport?.addEventListener('resize', fitRoot);
window.addEventListener('orientationchange', () => setTimeout(fitRoot, 300));
setInterval(fitRoot, 1000);   // iOS sometimes settles its toolbars without a resize event
fitRoot();
installInputLockReset(Phaser);   // a reused scene never starts locked (ui/inputLock.js)
gameFontReady.then(() => refreshTexts(game));
if (import.meta.env.DEV) import('./dev/fontSwitcher.js').then((m) => m.installFontSwitcher(game));   // [ / ] cycle trial fonts

setSessionLostHandler(() => { if (window.__myId) sessionLost(topScene(game)); });   // once: it clears __myId

mountAudioControls(document.body, { onSettings: () => openSettings(topScene(game)) });
mountMobileShell({ isLocked: () => game.scene.getScenes(true).some((s) => isInputLocked(s)) });
mountRotatePrompt();
installDiagBeacon(game);   // ?diag=1: phone memory heartbeat to the server log

// Track local AFK state (spec 2026-10-22): after 1 min of no input, or at once when the tab is
// hidden, show our own Zzz and tell the room; after 5 min close the socket (others stop seeing us,
// friends see us offline) and grey the game under the "Przerwa" card until a click, tap or key.
// A Game Hall match being played holds off the 5-minute stage: a chess player may think that long.
idleTracker.suppressInactive = () => matchHoldsInactive(matchClient.info);
idleTracker.onStage((stage, prev) => {
  for (const step of inactivityEffects(stage, prev, { loggedIn: !!window.__myId })) {
    if (step === 'pause') realtime.pause();
    else if (step === 'showOverlay') showInactiveOverlay();
    else if (step === 'hideOverlay') hideInactiveOverlay();
    else if (step === 'resume') realtime.resume();
    else if (step === 'refreshFriends') {
      // presence changes were missed while we were off the server
      document.getElementById('friends-panel')?._render?.()?.catch?.(() => {});
    }
  }
});
idleTracker.init();
idleTracker.on((idle) => realtime.idle(idle));
installAutoUpdate(game, idleTracker);   // a new deploy reaches open tabs at the next idle moment

// Dev handle — pozwala m.in. testom e2e wejść wprost do sceny (np. okno.__game.scene.start(...)).
if (import.meta.env.DEV) {
  window.__game = game;
  window.__gameState = gameState;  // e2e: inspect raw / exercise the response-ordering guard
  window.__idle = idleTracker;  // e.g. __idle.idleMs = 3000; __idle.inactiveMs = 8000 to test Zzz / Przerwa quickly
  window.__rt = realtime;       // e2e: drive table_sit/stand/select_game + inspect the socket
  window.__vin = virtualInput;  // e2e: inspect/invoke the current proximity action
  // Dev hotkeys (don't fire while typing in a chat/login field):
  //   q — +1000 money (needs backend DEV_CHEATS=true)
  //   t — freeze / unfreeze the day-night cycle at the current time of day
  //   r — advance the time of day one step (night → morning → day → evening), and freeze there
  const PHASES = ['night', 'morning', 'day', 'evening'];
  const PHASE_PL = { night: 'noc', morning: 'rano', day: 'dzień', evening: 'wieczór' };

  // Fixed badge shown whenever the day-night cycle is frozen via T/R.
  const timeBadge = document.createElement('div');
  timeBadge.style.cssText = [
    'position:fixed', 'top:78px', 'left:12px', 'z-index:1000', 'display:none',
    'background:#000000cc', 'color:#fff', 'font:bold 16px system-ui',
    'padding:6px 10px', 'border-radius:8px', 'pointer-events:none', 'user-select:none',
  ].join(';');
  document.body.appendChild(timeBadge);
  const updateTimeBadge = () => {
    if (gameState._devPhase) {
      timeBadge.innerHTML = iconHtml(`{pause} czas zatrzymany: ${PHASE_PL[gameState._devPhase] ?? gameState._devPhase}`);
      timeBadge.style.display = 'block';
    } else {
      timeBadge.style.display = 'none';
    }
  };

  window.addEventListener('keydown', (e) => {
    const tag = document.activeElement?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA') return;
    const k = e.key.toLowerCase();
    if (k === 'q') {
      gameState.devAddMoney().catch(() => {});
    } else if (k === 't') {
      gameState._devPhase = gameState._devPhase ? null : (gameState.raw?.time?.phase ?? 'day');
      gameState.emit();
      updateTimeBadge();
    } else if (k === 'r') {
      const cur = gameState._devPhase ?? gameState.raw?.time?.phase ?? 'night';
      gameState._devPhase = PHASES[(PHASES.indexOf(cur) + 1) % PHASES.length];
      gameState.emit();
      updateTimeBadge();
    }
  });
}
